import type { ReactNode } from "react";
import styles from "./Text.module.css";

/** The eleven semantic styles from §6.2. There is no twelfth. */
export type TextVariant =
  | "largeTitle"
  | "title"
  | "title2"
  | "title3"
  | "headline"
  | "body"
  | "callout"
  | "subheadline"
  | "footnote"
  | "caption"
  | "caption2";

export type TextTone =
  | "primary"
  | "secondary"
  | "tertiary"
  | "signal"
  | "interaction"
  | "destructive"
  | "warning"
  | "context"
  | "canvas";

export type TextWeight = "regular" | "medium" | "semibold" | "bold";

export type TextElement =
  | "span"
  | "p"
  | "div"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "strong"
  | "dt"
  | "dd"
  | "li";

export interface TextProps {
  readonly children: ReactNode;
  readonly variant?: TextVariant;
  readonly tone?: TextTone;
  /** Overrides the variant's own weight. `headline` is semibold by definition. */
  readonly weight?: TextWeight;
  /** Monospaced *and* tabular — the two always travel together. */
  readonly mono?: boolean;
  /** Tabular figures without switching family, for sans-serif metric columns. */
  readonly tabularNums?: boolean;
  readonly uppercase?: boolean;
  /**
   * The element is chosen separately from the style on purpose. A heading that
   * looks like a caption is common in this product; a caption that *is* an
   * `<h2>` in the accessibility tree when it is not a heading is a defect.
   */
  readonly as?: TextElement;
  readonly id?: string;
  readonly className?: string;
  readonly testId?: string;
}

function classNames(...values: readonly (string | false | undefined)[]): string {
  return values.filter((value): value is string => Boolean(value)).join(" ");
}

/**
 * The type styles from §6.2, as one component.
 *
 * Text is a primitive here because the product's hierarchy is carried by type
 * and spacing rather than by outlines and cards — so a surface that reaches for
 * a raw `font-size` is not making a local decision, it is stepping outside the
 * hierarchy the whole design depends on.
 */
export function Text({
  children,
  variant = "body",
  tone = "primary",
  weight,
  mono = false,
  tabularNums = false,
  uppercase = false,
  as = "span",
  id,
  className,
  testId,
}: TextProps) {
  const Element = as;
  return (
    <Element
      id={id}
      data-testid={testId}
      className={classNames(
        styles.text,
        styles[variant],
        styles[tone],
        weight !== undefined && styles[weight],
        mono && styles.mono,
        !mono && tabularNums && styles.tabular,
        uppercase && styles.uppercase,
        className,
      )}
    >
      {children}
    </Element>
  );
}
